import * as types from '../mutations'
import AxiosInstance from '@/utils/axios.instances.js'
import AIService from '@/services/ai'
import AuthService from '@/services/auth'
import Plans from '@/const/plans'

const state = {
  me: {},
  scantrustToken: '',
  error: false,
  errorMessage: '',
  companyVision: false,
  refreshingToken: false,
  pendingRequests: [],
  forceTokenRefresh: false
}

const getters = {
  isSelfServe: (state) => {
    if (state.me && state.me.plan) {
      return state.me.plan.interface?.mode === 'st-pro'
    }
  },
  company: (state) => state.me.company,
  scantrustAuthToken: (state) => state.scantrustToken,
  scantrustAuthError: (state) => state.error,
  scantrustAuthErrorMessage: (state) => state.errorMessage,
  companyVision: (state) => state.companyVision,

  isAdmin: (state) => {
    return state.me.is_staff
  },

  isAllowed: (state) => (action) => {
    if (!state.me || !state.me.permissions) return false

    const permissions = state.me.permissions

    return permissions.includes(action) || state.me.is_staff
  },

  isAvailable: (state, getters) => (feature) => {
    // Check against new plan feature. If plan do not exist, fall back to existing permission system.
    //
    if (!state.me.plan || !state.me.company) {
      return false
    }

    if (state.me.plan && state.me.plan.id !== '@not-set') {
      return (state.me.plan.interface && state.me.plan.interface[feature]) || (getters.isAdmin && !getters.companyVision)
    } else {
      // Old check
      if (!Plans[state.me.company.plan] || (getters.isAdmin && !getters.companyVision)) {
        return true
      }

      return Plans[state.me.company.plan].features[feature] && Plans[state.me.company.plan].features[feature].is_available
    }
  },

  hasLimit: (state, getters) => (feature) => {
    if (!state.me.plan || !state.me.company) {
      return false
    }

    if (state.me.plan && state.me.plan.id !== '@not-set') {
      if (getters.isAdmin && !getters.companyVision) {
        return false
      } else {
        return (state.me.plan.interface && state.me.plan.interface[feature] && typeof state.me.plan.interface[feature] === 'number')
      }
    } else {
      // Old check
      if (!Plans[state.me.company.plan] || (getters.isAdmin && !getters.companyVision)) {
        return true
      }

      return Plans[state.me.company.plan].features[feature] && Plans[state.me.company.plan].features[feature].has_limit
    }
  },

  getLimit: (state, getters) => (feature) => {
    if (!state.me.plan || !state.me.company) {
      return 0
    }

    if (state.me.plan && state.me.plan.id !== '@not-set') {
      if (getters.isAdmin && !getters.companyVision) {
        return 999
      } else if (state.me.plan.interface && state.me.plan.interface[feature] && typeof state.me.plan.interface[feature] === 'number') {
        return state.me.plan.interface[feature]
      }
    } else {
      // Old check
      if (!Plans[state.me.company.plan] || (getters.isAdmin && !getters.companyVision)) {
        return 999
      }

      return Plans[state.me.company.plan].features[feature] && Plans[state.me.company.plan].features[feature].limit
    }
  },

  currentPlan: (state) => {
    if (state.me.plan && state.me.plan.id !== '@not-set') {
      return state.me.plan
    }
    if (state.me.company) {
      return { id: state.me.company.plan }
    }
  },

  isRefreshingToken: (state) => {
    return state.refreshingToken
  },

  tokenRefreshRequests: (state) => {
    return state.pendingRequests
  },

  isTokenRefreshForced: (state) => {
    return state.forceTokenRefresh
  }
}

const actions = {
  setScantrustAuthToken ({ commit }, token) {
    AxiosInstance.setSTAuthToken(token)
    AIService.setAuthToken(token)
    commit(types.SCANTRUST_AUTH_SUCCESS, token)
  },

  async loadUserProfile ({ commit }) {
    const profile = await AuthService.loadProfile()
    commit(types.SET_USER_PROFILE, profile)
    return profile
  },

  scantrustAuthError ({ commit }, err) {
    commit(types.SCANTRUST_AUTH_ERROR, err)
  },

  setIsRefreshingToken ({ commit }, err) {
    commit(types.SET_IS_REFRESHING_TOKEN, err)
  },

  resetPendingRequest ({ commit }, err) {
    commit(types.RESET_PENDING_REQUEST, err)
  },

  addPendingRequest ({ commit }, err) {
    commit(types.ADD_PENDING_REQUEST, err)
  },

  processPendingRequest ({ commit }, err) {
    commit(types.PROCESS_PENDING_REQUEST, err)
  },

  forceTokenRefresh ({ commit }, err) {
    commit(types.FORCE_TOKEN_REFRESH)
  }
}

const mutations = {
  [types.SCANTRUST_AUTH_SUCCESS] (state, token) {
    state.scantrustToken = token
  },

  [types.SCANTRUST_AUTH_ERROR] (state, error) {
    if (error & error.status === 401) {
      state.error = true
      if (state.scantrustToken) {
        state.errorMessage = 'Invalid Token, please refresh your browser'
      } else {
        state.errorMessage = ' Authentication Token not provided'
      }
    }
  },

  [types.SET_USER_PROFILE] (state, profile) {
    state.me = profile
  },

  [types.CHANGE_COMPANY_VISION] (state, status) {
    state.companyVision = status
  },

  [types.SET_IS_REFRESHING_TOKEN] (state, isRefreshing) {
    state.refreshingToken = isRefreshing
  },

  [types.ADD_PENDING_REQUEST] (state, request) {
    state.pendingRequests = [...state.pendingRequests, request]
  },

  [types.RESET_PENDING_REQUEST] (state) {
    state.pendingRequests = []
  },

  [types.PROCESS_PENDING_REQUEST] (state, token) {
    state.pendingRequests.forEach((request) => {
      request.resolve(token)
    })
    state.pendingRequests = []
  },

  [types.FORCE_TOKEN_REFRESH] (state, forceRefresh) {
    state.forceTokenRefresh = forceRefresh
  }

}

export default {
  state,
  getters,
  actions,
  mutations
}
